import * as React from 'react';
import { useEffect, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css } from '@coursera/coursera-ui';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import Alert from 'bundles/authentication/shared/Alert';
import AuthenticationV3CtaTracking from 'bundles/authentication/shared/AuthenticationV3CtaTracking';
import LoginForm from 'bundles/authentication/shared/LoginForm';
import Separator from 'bundles/authentication/shared/Separator';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import SocialSSO from 'bundles/authentication/shared/components/authentication-modal/SocialSSO';
import type SSOError from 'bundles/authentication/shared/components/authentication-modal/SocialSSO/SSOError';
import type { ThirdPartyOrganizationsData } from 'bundles/authentication/shared/hooks/useUserThirdPartyOrganizationsQuery';
import type { ArkoseBotManagerContext } from 'bundles/authentication/shared/types/sharedTypes';
import { SSO_VARIANT, getSSOVariant } from 'bundles/authentication/utils/ssoUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import SSOButton from 'bundles/user-account/components/SsoButton';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/authentication';

type Props = {
  className?: string;
  defaultEmail?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  onLogin: () => void;
  onSSOError: (error: SSOError) => void;
  onSSOSuccess: (isRegistration: boolean) => void;
  preventRegister?: boolean;
  redirect?: string;
  arkoseBotManagerContext?: ArkoseBotManagerContext;
  isEnterprise?: boolean;
  shouldShowCustomSSO?: boolean;
  forceSSOLogin?: boolean;
  authProvider?: string;
} & ThirdPartyOrganizationsData;

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);

const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '32px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
    maxWidth: '396px',
    padding: '32px 16px',
    '@media (min-width: 600px)': {
      padding: '12px 16px',
    },
  },
  navigation: {
    margin: '16px 0 0',
    textAlign: 'center',
  },
  link: {
    textDecoration: 'underline',
  },
  organization: {
    margin: '0',
    textAlign: 'center',
  },
  section: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
  },
  separator: {
    margin: '24px 0',
  },
  title: {
    color: '#1F1F1F',
    fontFamily: 'OpenSans',
    fontSize: '1.5rem',
    lineHeight: '2rem',
    textAlign: 'center',
    '@media (min-width: 600px)': {
      fontSize: '1.75rem',
      lineHeight: '2.25rem',
    },
  },
  terms: {
    color: '#616161',
    fontSize: '12px',
    lineHeight: '20px',
    margin: '16px 0 0',
    padding: '0 32px',
    textAlign: 'center',
    ':first-of-type': {
      margin: '0',
    },
    // workaround for nested selectors
    ':nth-of-type(n) a': {
      color: 'inherit',
      textDecoration: 'underline',
    },
  },
});

const Login = ({
  navigate,
  onLogin,
  onSSOSuccess,
  className,
  defaultEmail,
  onSSOError,
  preventRegister,
  arkoseBotManagerContext,
  isEnterprise,
  shouldShowCustomSSO,
  forceSSOLogin,
  organizations,
  authProvider,
}: Props) => {
  const [hasError, setHasError] = useState(false);

  const navigateToForgotPassword = () => {
    navigate(AUTHENTICATION_MODAL_MODES.FORGOT);
  };

  const navigateToRequestMagicLink = () => {
    navigate(AUTHENTICATION_MODAL_MODES.REQUESTMAGICLINK);
  };

  const navigateToOrganizationSSO = () => {
    navigate(AUTHENTICATION_MODAL_MODES.ORGANIZATIONLOGIN, { referrer: AUTHENTICATION_MODAL_MODES.LOGIN });
  };

  const navigateToSignup = () => {
    navigate(AUTHENTICATION_MODAL_MODES.SIGNUP);
  };

  const onLoginError = () => {
    setHasError(true);
  };

  const handleSuccess = (redirectURL: string) => {
    Instrumentation.login();

    onLogin();
  };

  const handleSSOSuccess = (isRegistration: boolean) => {
    onSSOSuccess(isRegistration);
  };

  const isInvisible = GrowthAcquisitionExperiments.get('useEnterpriseInvisibleReCaptcha');
  const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaLogin');
  const shouldShowEnterpriseRecaptchaDisclaimer = isInvisible || isCheckbox;

  const hideForm = organizations?.[0]?.supportsDegreeSSOLogin || forceSSOLogin;

  const ssoVariant = getSSOVariant({ authProvider, isEnterprise });

  return (
    <section {...css(className, styles.container)} data-catchpoint="authentication-box-content">
      <header {...css(styles.section)}>
        <h1 {...css(styles.title)}>{_t('Welcome back')}</h1>
      </header>
      <div {...css(styles.section)}>
        {hasError && (
          <Alert type="error">
            <FormattedMessage
              message={_t('An unexpected error has occurred. Please try again or contact our {helpCenterLink}.')}
              helpCenterLink={<a href="https://courserahelp.zendesk.com">{_t('Learner Help Center')}</a>}
            />
          </Alert>
        )}
        {/* TODO: Consolidate the whitelist organization for SSO login logic to `getSSOVariant` utility */}
        {authProvider && shouldShowCustomSSO && (
          <>
            <SSOButton provider={authProvider} organizations={organizations} />
            {!forceSSOLogin && <Separator {...css(styles.separator)} text={_t('or')} />}
          </>
        )}
        {!hideForm && (
          <LoginForm
            defaultEmail={defaultEmail}
            onError={onLoginError}
            onForgotPassword={navigateToForgotPassword}
            onRequestMagicLink={navigateToRequestMagicLink}
            onSuccess={handleSuccess}
            arkoseBotManagerContext={arkoseBotManagerContext}
          />
        )}
        {ssoVariant === SSO_VARIANT.SOCIAL && (
          <>
            <Separator {...css(styles.separator)} text={_t('or')} />
            <SocialSSO onError={onSSOError} onSuccess={handleSSOSuccess} />
          </>
        )}
        {!preventRegister && (
          <p {...css(styles.navigation)}>
            <FormattedMessage
              message={_t('New to Coursera? {signupLink}')}
              signupLink={
                <AuthenticationV3CtaTracking>
                  {(trackV3) => {
                    const handleOnClick = () => {
                      trackV3({ name: 'signup' });
                      navigateToSignup();
                    };
                    return (
                      <TrackedLinkButton onClick={handleOnClick} trackingName="signup" {...css(styles.link)}>
                        {_t('Sign up')}
                      </TrackedLinkButton>
                    );
                  }}
                </AuthenticationV3CtaTracking>
              }
            />
          </p>
        )}
        {ssoVariant === SSO_VARIANT.SOCIAL && (
          <>
            {/* @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351 */}
            <Separator {...css(styles.separator)} />
            <p {...css(styles.organization)}>
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'signup_with_other_sso' });
                    navigateToOrganizationSSO();
                  };
                  return (
                    <TrackedLinkButton
                      onClick={handleOnClick}
                      trackingName="sign_up_with_organization_btn_click"
                      {...css(styles.link)}
                    >
                      {_t('Log in with your organization')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            </p>
          </>
        )}
      </div>
      <div {...css(styles.section)}>
        <p {...css(styles.terms)}>
          <FormattedMessage
            message={_t('Having trouble logging in? {helpCenterLink}')}
            helpCenterLink={
              <a href="https://learner.coursera.help/hc/articles/209818553" target="_blank" rel="noopener noreferrer">
                {_t('Learner help center')}
              </a>
            }
          />
        </p>
        {!forceSSOLogin && (
          <p {...css(styles.terms)}>
            <FormattedMessage
              message={
                shouldShowEnterpriseRecaptchaDisclaimer
                  ? _t(
                      'This site is protected by reCAPTCHA Enterprise and the Google {privacyPolicyLink} and {tosLink} apply.'
                    )
                  : _t('This site is protected by reCAPTCHA and the Google {privacyPolicyLink} and {tosLink} apply.')
              }
              privacyPolicyLink={
                <TrackedA href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  {_t('Privacy Policy')}
                </TrackedA>
              }
              tosLink={
                <TrackedA href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                  {_t('Terms of Service')}
                </TrackedA>
              }
            />
          </p>
        )}
      </div>
    </section>
  );
};

export default Login;
